import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { AuthContext } from "../contexts/AuthContext";
import { Menu, Transition } from "@headlessui/react";
import { FiSettings, FiLogOut, FiChevronDown } from "react-icons/fi";
import { FaHome, FaCubes, FaDonate, FaEnvelope, FaBars } from "react-icons/fa";
import { motion } from "framer-motion";
import logo from "../assets/images/logo.png";
import profilePlaceholder from "../assets/images/profile-placeholder.jpg";

const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const pathLang = location.pathname.split("/")[1];
  const validLangs = ["en", "ar"];
  const initialLang = validLangs.includes(pathLang) ? pathLang : localStorage.getItem("i18nextLng") || "en";
  const [currentLang, setCurrentLang] = useState(initialLang);
  const [profileLoading, setProfileLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem("i18nextLng") || "en";
    if (savedLang !== currentLang) {
      setCurrentLang(savedLang);
      i18n.changeLanguage(savedLang);
    } else if (validLangs.includes(pathLang) && pathLang !== currentLang) {
      setCurrentLang(pathLang);
      localStorage.setItem("i18nextLng", pathLang);
      i18n.changeLanguage(pathLang);
    }
  }, [location, pathLang]);

  const changeLanguage = async (lng) => {
    setIsSwitching(true);
    try {
      await i18n.changeLanguage(lng);
      localStorage.setItem("i18nextLng", lng);
      setCurrentLang(lng);
      const currentPath = location.pathname.replace(/^\/(en|ar)?\/?/, "");
      navigate(`/${lng}${currentPath ? "/" + currentPath : ""}`, { replace: true });
    } catch (error) {
      console.error("Language switch error:", error);
    } finally {
      setTimeout(() => setIsSwitching(false), 500);
    }
  };

  const handleProfileClick = () => {
    setProfileLoading(true);
    setTimeout(() => {
      navigate(`/${currentLang}/profile`);
      setProfileLoading(false);
    }, 1000);
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate(`/${currentLang}`);
    } finally {
      setLogoutLoading(false);
    }
  };

  const hamburgerVariants = {
    open: { rotate: 90, scale: 1.2 },
    closed: { rotate: 0, scale: 1 },
  };

  return (
    <>
      {isSwitching && (
        <div className="lang-switch-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <motion.nav
        className="navbar-glass p-4 flex flex-col md:flex-row justify-between items-center sticky top-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center w-full md:w-auto">
          <motion.div
            className="text-2xl font-bold"
            whileHover={{ scale: 1.1 }}
          >
            <Link to={`/${currentLang}`}>
              <img src={logo} alt="BRT Logo" className="h-10" />
            </Link>
          </motion.div>
          <motion.button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            animate={isMobileMenuOpen ? "open" : "closed"}
            variants={hamburgerVariants}
            transition={{ duration: 0.3 }}
          >
            <FaBars className="text-2xl text-white" />
          </motion.button>
        </div>
        <motion.div
          className={`flex-col md:flex-row md:flex md:items-center w-full md:w-auto space-y-4 md:space-y-0 md:space-x-6 mt-4 md:mt-0 ${isMobileMenuOpen ? "flex" : "hidden md:flex"}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to={`/${currentLang}`} className="flex items-center text-white hover:text-gray-200 transition-all">
              <FaHome className="mr-2" /> {t("navbar.home")}
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to={`/${currentLang}/services`} className="flex items-center text-white hover:text-gray-200 transition-all">
              <FaCubes className="mr-2" /> {t("navbar.services")}
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to={`/${currentLang}/donate`} className="flex items-center text-white hover:text-gray-200 transition-all">
              <FaDonate className="mr-2" /> {t("navbar.donate")}
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to={`/${currentLang}/contact`} className="flex items-center text-white hover:text-gray-200 transition-all">
              <FaEnvelope className="mr-2" /> {t("navbar.contact")}
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to={`/${currentLang}/update`} className="flex items-center text-white hover:text-gray-200 transition-all">
              <FaCubes className="mr-2" /> {t("navbar.update")}
            </Link>
          </motion.div>
        </motion.div>
        <div className="flex space-x-4 items-center mt-4 md:mt-0">
          {!user && (
            <>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link to={`/${currentLang}/signin`} className="text-white hover:text-gray-200 transition-all">
                  {t("navbar.signin")}
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link to={`/${currentLang}/signup`} className="text-white hover:text-gray-200 transition-all">
                  {t("navbar.signup")}
                </Link>
              </motion.div>
            </>
          )}
          {user && (
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center text-white hover:text-gray-200 transition-all">
                <motion.img
                  src={user.photoURL || profilePlaceholder}
                  alt="Profile"
                  className="w-8 h-8 border-2 border-white rounded-full mr-2"
                  whileHover={{ scale: 1.1 }}
                  onError={(e) => {
                    console.error("Profile image failed to load:", e);
                    e.target.src = profilePlaceholder;
                  }}
                />
                <FiChevronDown />
              </Menu.Button>
              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 secondary-bg glass rounded-md shadow-lg py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleProfileClick}
                        className={`${
                          active ? "bg-gray-700 text-white" : "text-white"
                        } flex items-center px-4 py-2 text-sm w-full text-left hover:text-gray-200`}
                        disabled={profileLoading}
                      >
                        {profileLoading ? (
                          <div className="loading-spinner mr-2"></div>
                        ) : (
                          <FiSettings className="mr-2" />
                        )}
                        {t("navbar.profile")}
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? "bg-gray-700 text-white" : "text-white"
                        } flex items-center px-4 py-2 text-sm w-full text-left hover:text-gray-200`}
                        disabled={logoutLoading}
                      >
                        {logoutLoading ? (
                          <div className="loading-spinner mr-2"></div>
                        ) : (
                          <FiLogOut className="mr-2" />
                        )}
                        {t("navbar.logout")}
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          )}
          <motion.select
            onChange={(e) => changeLanguage(e.target.value)}
            value={currentLang}
            className="bg-transparent border border-white rounded p-1 text-white text-sm"
            disabled={isSwitching}
            whileHover={{ scale: 1.05 }}
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </motion.select>
        </div>
      </motion.nav>
    </>
  );
};

export default Navbar;